import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import styles from "./quickLinkPopup.module.css";
import { EditButton, Submit } from '../../../../../components/button/buttons';
import { useState } from 'react';
import IMAGES from '../../../../../assets/images/images';
import Asterisk from '../../../../../components/asterisk/Asterisk';
import { useBodyScrollLock } from '../../../../../utils/utils';
import { FormImgAvailableLabel } from '../../../../../components/label/Labels';

const QuickLinkPopup = ({action, setIsPopupOpen, isPopupOpen, AddQuickLink, EditQuickLink, DeleteQuickLink, openAlertPopup,getQuickLinks,linkDetail,setLinkDetail,links }) => {
  const [isSubmit,setIsSubmit]=useState(false);
  const initialValues = {
    appName: linkDetail?.item?.title || '',
    appLink: linkDetail?.item?.link_url || '',
  };
  
  useBodyScrollLock(isPopupOpen);

  const checkIfExists = (value, field) => {
    if (!value) return;
    const exists = links.some(link => link[field]?.toLowerCase() === value.toLowerCase() && link?.linkId!==linkDetail?.item?.linkId);
    return exists ? `${field === 'title' ? 'Name' : 'Link'} already exists. Try another` : undefined;
  };

  
  const validationSchema = Yup.object({
    appName: Yup.string()
    .max(50,'Name must be at most 50 characters.')
    .required('Name is required') 
    .test('unique-title', `Name already exists. Try another`, function (value) {
      return !links.some(link => link.title?.toLowerCase() === value?.toLowerCase() &&link?.linkId!==linkDetail?.item?.linkId);
    }),
    appLink: Yup.string()
      .matches(/^https:\/\/.*$/, 'Only HTTPS links are allowed.')
      .url('Enter a valid URL')
      .required('Link is required')
      .test('unique-link', 'Link already exists. Try another', function (value) {
        return !links.some(link => link.link_url?.toLowerCase() === value?.toLowerCase()&&link?.linkId!==linkDetail?.item?.linkId);
      }),
  });
  
  const handleSubmit = (values) => {
    setIsSubmit(true)
    let obj = {
      title:values?.appName,
      link_url:values?.appLink,
    }
    const actionToCall = linkDetail?.isEditMode ? EditQuickLink : AddQuickLink;
    actionToCall(obj,linkDetail?.item?.linkId).then((result) => {      
      if (result.status) {
        openAlertPopup({
          message: linkDetail?.isEditMode ? "Quick link updated successfully!" : "Quick link added successfully!",
          image: IMAGES.success
        });
        setIsSubmit(false);
        setIsPopupOpen(false);
        setLinkDetail({isEditMode:false,isDeleteMode:false,item:{}});
        getQuickLinks(action);
      } else {
        let error_msg = result?.error ?? "Something went wrong.";
        openAlertPopup({
          message: error_msg,
          image: IMAGES.error
        });
        setIsSubmit(false); 
        setIsPopupOpen(false)
        setLinkDetail({isEditMode:false,isDeleteMode:false,item:{}});
      }
    })
      .catch((error) => {
        openAlertPopup({
          message: "Something went wrong.",
          image: IMAGES.error
        });
        setIsSubmit(false);
      });
  };

  const deleteLink = async (id) => {
    setIsSubmit(true)
    try {
      const result = await DeleteQuickLink(id);
      if (result?.status) {
        openAlertPopup({
          message: "Quick link deleted successfully!",
          image: IMAGES.success
        });
        getQuickLinks(action);
        setIsSubmit(false);
      } else {
        const error_msg = result?.error?.message ?? "Something went wrong.";
        openAlertPopup({
          message: error_msg,
          image: IMAGES.error
        });
      }
      setIsSubmit(false);
    } catch (error) {
      openAlertPopup({
        message: "Something went wrong.",
        image: IMAGES.error
      });
    }
    setIsSubmit(false);
    setIsPopupOpen(false);
    setLinkDetail({isEditMode:false,isDeleteMode:false,item:{}});
  };

  return (
    <div className={styles.alertmessage}>
      <div className={`${linkDetail?.isDeleteMode?styles.deletecontent:styles.alertcontent}`}>
        <div className={styles.header}>
          <div className={styles.headertxt}>{linkDetail?.isEditMode ? "Edit Quick Link" :linkDetail?.isDeleteMode ? "Delete Link" : "Add Quick Link"}</div>
          {linkDetail?.isEditMode && <img src={IMAGES.close} className={styles.closeicon} alt="close icon" onClick={()=>{setIsPopupOpen(false);setLinkDetail({isEditMode:false,isDeleteMode:false,item:{}});}} />}
        </div>
        {linkDetail?.isDeleteMode ? (
          <div className={styles.deleteConfirm}>
            <p className={styles.confirmationtext}>Are you sure you want to delete '{linkDetail?.item?.title}'?</p>
            <div className={styles.buttonwrapper}>
              <EditButton
              title="No, Cancel"
              onClick={()=>{setIsPopupOpen(false);setLinkDetail({isEditMode:false,isDeleteMode:false,item:{}});}}/>
              <Submit
              title="Yes, Delete"
              isSubmitting={isSubmit}
              onClick={() => deleteLink(linkDetail?.item?.linkId)}
              />
              </div>
            </div>) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ dirty, isValid, values, touched, errors }) => (
          <Form >
            <div className={styles.formGroup}>
              <label className={styles.inputlabel} htmlFor="appName">Name <Asterisk /></label>
              <Field 
                name="appName"
                validate={(value) => checkIfExists(value, 'title')}
                type="text"
                className={styles.input} placeholder={"Enter name"} />
               <div className={styles.row}>
                  <div className={styles.leftBox}>
                     <ErrorMessage name="appName" component={({ children }) => (<div className={styles.error}><img src={IMAGES.Vector} alt="error" className={styles.errorimg} /><span>{children}</span></div>)}/>
                     {touched.appName && values.appName && !errors.appName && values.appName !== linkDetail?.item?.title && !checkIfExists(values.appName, 'title') && (
                     <FormImgAvailableLabel text="Available" />
                     )}
                  </div>
                  <div className={styles.rightBox}>{`${values?.appName?.length}/50 characters`}</div>
                </div>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.inputlabel} htmlFor="appLink">Link <Asterisk /></label>
              <Field 
                name="appLink"
                validate={(value) => checkIfExists(value, 'link_url')}
                type="text" 
                className={styles.input} placeholder={"Enter link"} />
              <ErrorMessage name="appLink" component={({ children }) => (<div className={styles.error}><img src={IMAGES.Vector} alt="error" className={styles.errorimg} /><span>{children}</span></div>)}/>
              {touched.appLink && values.appLink && !errors.appLink && values.appLink !== linkDetail?.item?.link_url && !checkIfExists(values.appLink, 'link_url') && (
                <FormImgAvailableLabel text="Available" />
              )}
            </div>
            <div className={styles.buttonwrapper}>
                {linkDetail?.isEditMode?
                <EditButton
                className={styles.backButton}
                title="Delete Link"
                style={styles.btn}
                onClick={()=>!isSubmit&&setLinkDetail(prevState => ({...prevState,isEditMode:false,isDeleteMode:true}))}/>
                :<EditButton
                className={styles.backButton}
                title="Cancel"
                style={styles.btn}
                onClick={()=>{setIsPopupOpen(false);setLinkDetail({isEditMode:false,isDeleteMode:false,item:{}});}}/>}
                <Submit
                style={styles.submit}
                title={linkDetail?.isEditMode ? "Edit Link" : "Add Link"}
                isSubmitting={isSubmit}
                disabled={!dirty || !isValid}/>
            </div>
          </Form>
          )}
        </Formik>)}
      </div>
    </div>
  );
};

export default QuickLinkPopup;
